/**
 * Capability-based access control components.
 *
 * These sit alongside the existing role-based `FeatureAuth` but work at the
 * finer-grained PBAC capability level (e.g. 'org:manage_settings').
 *
 * TWO components:
 *
 * 1. `CapabilityAuth` — for sidebar items and UI sections.
 *    Renders `null` (silently hides) if the user does not hold the capability.
 *    Does NOT redirect.
 *
 * 2. `RequireCapability` — for route-level protection.
 *    Redirects to `/access-denied` (or a custom `redirectTo`) if the user does
 *    not hold the capability, even if they arrive via direct URL.
 *
 * `platform:manage_system_admins` is treated like any other capability: holders
 * pass the same checks via `hasCapability` (no separate profile flag bypass).
 *
 * @example Sidebar item
 *   <CapabilityAuth capability="org:manage_settings">
 *     <LeftNavMenuItem icon={SettingsIcon}>Settings</LeftNavMenuItem>
 *   </CapabilityAuth>
 *
 * @example Route element
 *   {
 *     path: '/governance',
 *     element: (
 *       <RequireCapability capability="org:manage_policies">
 *         <GovernancePage />
 *       </RequireCapability>
 *     ),
 *   }
 *
 * @example Multiple capabilities (AND logic)
 *   <CapabilityAuth capabilities={['org:manage_members', 'org:manage_settings']}>
 *     ...
 *   </CapabilityAuth>
 *
 * @example Any capability (OR logic)
 *   <CapabilityAuth anyCapability={['report:view', 'report:download']}>
 *     ...
 *   </CapabilityAuth>
 */
import React from 'react';
import { Navigate } from 'react-router-dom';

import { usePermissions } from '~/client/contexts/permissions-context';

// ── Shared logic ──────────────────────────────────────────────────────────────

interface SharedProps {
  /** Require this single capability (AND'd with `capabilities` if both given) */
  capability?: string;
  /** Require ALL of these capabilities */
  capabilities?: string[];
  /** Require ANY of these capabilities (OR logic) */
  anyCapability?: string[];
  children: React.ReactNode;
}

function useCapabilityCheck({
  capability,
  capabilities,
  anyCapability,
}: Omit<SharedProps, 'children'>): { allowed: boolean; loading: boolean } {
  const { hasCapability, loading } = usePermissions();

  // Platform operators (capability from matrix / overrides, not JWT flag)
  if (hasCapability('platform:manage_system_admins')) {
    return { allowed: true, loading: false };
  }

  const checks: boolean[] = [];

  if (capability) {
    checks.push(hasCapability(capability));
  }

  if (capabilities && capabilities.length > 0) {
    checks.push(capabilities.every((c) => hasCapability(c)));
  }

  if (anyCapability && anyCapability.length > 0) {
    checks.push(anyCapability.some((c) => hasCapability(c)));
  }

  // If no constraints, allow
  if (checks.length === 0) {
    return { allowed: true, loading };
  }

  return { allowed: checks.every(Boolean), loading };
}

// ── CapabilityAuth (sidebar / UI sections) ────────────────────────────────────

interface CapabilityAuthProps extends SharedProps {
  /**
   * What to render while permissions are loading.
   * Defaults to `null` (nothing) — safe for sidebar items.
   * Pass a skeleton if you want a visual placeholder.
   */
  fallback?: React.ReactNode;
}

/**
 * Renders `children` only if the user has the required capability.
 * Renders `fallback` (default: nothing) while loading or when access is denied.
 *
 * Use this for sidebar items, feature sections, and UI elements.
 * Does NOT redirect — it simply hides the content.
 */
export const CapabilityAuth: React.FC<CapabilityAuthProps> = ({
  capability,
  capabilities,
  anyCapability,
  children,
  fallback = null,
}) => {
  const { allowed, loading } = useCapabilityCheck({
    capability,
    capabilities,
    anyCapability,
  });

  if (loading) {
    return <>{fallback}</>;
  }
  if (!allowed) {
    return null;
  }
  return <>{children}</>;
};

// ── RequireCapability (route-level guard) ─────────────────────────────────────

interface RequireCapabilityProps extends SharedProps {
  /**
   * Where to redirect if access is denied.
   * Defaults to `/access-denied`.
   */
  redirectTo?: string;
  /**
   * What to render while permissions are loading.
   * Defaults to `null` — the page appears empty until the check resolves.
   */
  loadingFallback?: React.ReactNode;
}

/**
 * Route-level guard. Redirects to `redirectTo` if the user does not hold the
 * required capability, even if they arrive via a direct URL.
 *
 * Use this as the `element` wrapper for protected route entries in routes.tsx.
 */
export const RequireCapability: React.FC<RequireCapabilityProps> = ({
  capability,
  capabilities,
  anyCapability,
  children,
  redirectTo = '/access-denied',
  loadingFallback = null,
}) => {
  const { allowed, loading } = useCapabilityCheck({
    capability,
    capabilities,
    anyCapability,
  });

  if (loading) {
    return <>{loadingFallback}</>;
  }
  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};
